import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Container, Paper, Stack, Typography } from '@mui/material';
import DataTable from '../components/DataTable';
import AppPagination from '../components/AppPagination';
import { fetchHistorialPage } from '../api/transacciones';
import { useLoading } from '../contexts/LoadingContext';
import DateField from '../components/DateField';

export default function Transacciones() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [cursors, setCursors] = useState([null]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const { stop } = useLoading();
  const initialLoadRef = useRef(true);

  const loadPage = useCallback(async ({ cursor = null, targetPage = 0, limit = pageSize } = {}) => {
    setLoading(true);
    try {
      const { items, nextCursor: receivedCursor, hasMore: receivedHasMore } = await fetchHistorialPage({
        limit,
        cursor,
      });
      const nextItems = Array.isArray(items) ? items : [];
      const next = receivedCursor || null;

      setRows(nextItems);
      setNextCursor(next);
      setHasMore(Boolean(receivedHasMore && next));
      setPage(targetPage);
      setCursors((current) => {
        const updated = current.slice(0, targetPage + 1);
        updated[targetPage] = cursor;
        if (next) updated[targetPage + 1] = next;
        return updated;
      });
    } catch (error) {
      console.error('Error cargando historial', error);
    } finally {
      setLoading(false);
      if (initialLoadRef.current) {
        stop();
        initialLoadRef.current = false;
      }
    }
  }, [pageSize, stop]);

  useEffect(() => {
    loadPage({ cursor: null, targetPage: 0, limit: pageSize });
  }, [loadPage, pageSize]);

  const handlePageChange = (_event, nextPage) => {
    if (nextPage === page || loading) return;

    if (nextPage < page) {
      loadPage({ cursor: cursors[nextPage] ?? null, targetPage: nextPage });
      return;
    }

    if (nextPage === page + 1 && hasMore && nextCursor) {
      loadPage({ cursor: nextCursor, targetPage: nextPage });
    }
  };

  const handleRowsPerPageChange = (event) => {
    const nextSize = parseInt(event.target.value, 10) || 10;
    setPage(0);
    setPageSize(nextSize);
    setCursors([null]);
    setNextCursor(null);
    setHasMore(true);
  };

  const columns = [
    { Header: 'ID TX', accessor: 'id_transaccion' },
    { Header: 'Producto', accessor: 'producto_nombre' },
    { Header: 'Código orden', accessor: 'orden_codigo' },
    {
      Header: 'Tipo',
      accessor: (row) => (
        row.id_venta && row.estado_venta === 'cancelada'
          ? 'Venta cancelada'
          : row.tipo_transaccion
      ),
    },
    { Header: 'Precio', accessor: 'precio_transaccion' },
    { Header: 'Cantidad', accessor: 'cantidad_transaccion' },
    { Header: 'Fecha', accessor: (row) => <DateField value={row.fecha_transaccion} /> },
  ];

  const knownCount = hasMore ? -1 : page * pageSize + rows.length;

  return (
    <Container sx={{ mt: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Historial de transacciones</Typography>
      </Stack>
      <Paper sx={{ overflow: 'hidden' }}>
        <DataTable rows={rows} columns={columns} loading={loading} />
        <AppPagination
          count={knownCount}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          showLastButton={false}
        />
      </Paper>
    </Container>
  );
}
